import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import EditGroupModal from "./EditGroupModal";
import GroupCourseModal from "./GroupCourseModal";
import GroupProgramModal from "./GroupProgramModal";
import EditMembersModal from "./EditMembersModal";
import {
  IconEdit,
  IconArrowLeft,
  IconUsers,
  IconBook,
  IconSchool,
  IconUser,
} from "@tabler/icons-react";
import {
  Text,
  Button,
  Group,
  Stack,
  Paper,
  Title,
  Loader,
  Alert,
  Badge,
  Divider,
  ActionIcon,
  SimpleGrid,
} from "@mantine/core";
import { handleGenericError } from "../../utils/errorHandler";

function GroupDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getVerifiedJWT } = useAuth();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [repTypes, setRepTypes] = useState([]);

  // Separate loading states for each section
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);

  const [editGroupModalOpen, setEditGroupModalOpen] = useState(false);
  const [editCoursesModalOpen, setEditCoursesModalOpen] = useState(false);
  const [editProgramsModalOpen, setEditProgramsModalOpen] = useState(false);
  const [editMembersModalOpen, setEditMembersModalOpen] = useState(false);

  useEffect(() => {
    fetchGroupDetails();
    fetchRepTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check if user has permission to access group details page
  if (!user || (user.role !== "MANAGER" && user.role !== "COMPANY_ADMIN")) {
    return <Navigate to="/dashboard" replace />;
  }

  const fetchGroupInfo = async () => {
    try {
      const jwt = await getVerifiedJWT();

      const groupResponse = await fetch(
        getApiEndpoint(`/v1/groups/group/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!groupResponse.ok) {
        throw new Error("Failed to fetch group data");
      }

      const groupResult = await groupResponse.json();
      const groupData = groupResult.data;

      // Update only the group info, preserving existing courses/programs/users
      setGroup(prevGroup => ({
        ...prevGroup,
        ...groupData,
      }));

      return { jwt, groupData };
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const jwt = await getVerifiedJWT();

      // Fetch group basic info first and show page immediately
      const groupResponse = await fetch(
        getApiEndpoint(`/v1/groups/group/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (!groupResponse.ok) {
        throw new Error("Failed to fetch group data");
      }

      const groupResult = await groupResponse.json();
      const groupData = groupResult.data;

      // Set basic group data immediately to show the page
      setGroup({
        ...groupData,
        courses: [],
        programs: [],
        users: [],
        unadded_courses: [],
        unadded_programs: [],
        unadded_users: [],
      });
      setLoading(false); // Show page now

      // Fetch sections in parallel but update individually as they complete
      fetchGroupCourses(jwt, groupData);
      fetchGroupPrograms(jwt, groupData);
      fetchGroupUsers(jwt, groupData);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchGroupCourses = async (jwt, baseGroupData) => {
    try {
      setCoursesLoading(true);
      const coursesResponse = await fetch(
        getApiEndpoint(`/v1/groups/group-courses/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (coursesResponse.ok) {
        const coursesResult = await coursesResponse.json();
        const courses = coursesResult.data?.courses || coursesResult.data || [];
        const unadded_courses = coursesResult.data?.unadded_courses || [];

        setGroup(prevGroup => ({
          ...prevGroup,
          courses,
          unadded_courses,
        }));
      }
    } catch (error) {
      handleGenericError(error, "Failed to fetch courses");
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchGroupPrograms = async (jwt, baseGroupData) => {
    try {
      setProgramsLoading(true);
      const programsResponse = await fetch(
        getApiEndpoint(`/v1/groups/group-programs/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (programsResponse.ok) {
        const programsResult = await programsResponse.json();
        const programs =
          programsResult.data?.programs || programsResult.data || [];
        const unadded_programs = programsResult.data?.unadded_programs || [];

        setGroup(prevGroup => ({
          ...prevGroup,
          programs,
          unadded_programs,
        }));
      }
    } catch (error) {
      handleGenericError(error, "Failed to fetch programs");
    } finally {
      setProgramsLoading(false);
    }
  };

  const fetchGroupUsers = async (jwt, baseGroupData) => {
    try {
      setUsersLoading(true);
      const usersResponse = await fetch(
        getApiEndpoint(`/v1/groups/group-users/${id}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (usersResponse.ok) {
        const usersResult = await usersResponse.json();
        const users = usersResult.data?.users || usersResult.data || [];
        const unadded_users = usersResult.data?.unadded_users || [];

        setGroup(prevGroup => ({
          ...prevGroup,
          users,
          unadded_users,
        }));
      }
    } catch (error) {
      handleGenericError(error, "Failed to fetch users");
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchRepTypes = async () => {
    try {
      const jwt = await getVerifiedJWT();
      const response = await fetch(getApiEndpoint(`/v2/company/rep-types`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setRepTypes(result.data || []);
      }
    } catch (error) {}
  };

  const handleEditGroup = () => {
    setEditGroupModalOpen(true);
  };

  const handleGroupUpdated = async () => {
    setEditGroupModalOpen(false);
    try {
      // When group details are edited, also reload members and group details
      const { jwt } = await fetchGroupInfo();
      fetchGroupUsers(jwt);
    } catch (error) {
      handleGenericError(error, "Failed to fetch group data");
    }
  };

  const handleGroupDeleted = () => {
    setEditGroupModalOpen(false);
    // Navigate back to admin page since the group was deleted
    navigate("/admin");
  };

  const handleEditCourses = () => {
    setEditCoursesModalOpen(true);
  };

  const handleCoursesUpdated = async () => {
    setEditCoursesModalOpen(false);
    try {
      // When courses are edited, only reload courses
      const jwt = await getVerifiedJWT();
      fetchGroupCourses(jwt);
    } catch (error) {
      handleGenericError(error, "Failed to fetch courses");
    }
  };

  const handleEditPrograms = () => {
    setEditProgramsModalOpen(true);
  };

  const handleProgramsUpdated = async () => {
    setEditProgramsModalOpen(false);
    try {
      // When programs are edited, only reload programs
      const jwt = await getVerifiedJWT();
      fetchGroupPrograms(jwt);
    } catch (error) {
      handleGenericError(error, "Failed to fetch programs");
    }
  };

  const handleEditMembers = () => {
    setEditMembersModalOpen(true);
  };

  const handleMembersUpdated = async () => {
    setEditMembersModalOpen(false);
    try {
      // When members are edited, only reload members
      const jwt = await getVerifiedJWT();
      fetchGroupUsers(jwt);
    } catch (error) {
      handleGenericError(error, "Failed to fetch members");
    }
  };

  // const handleSyncUsers = async () => {
  //   try {
  //     setSyncLoading(true);
  //     const jwt = await getVerifiedJWT();

  //     const response = await fetch(
  //       getApiEndpoint(`/organization/group/sync-users`),
  //       {
  //         method: "PUT",
  //         headers: {
  //           "Content-Type": "application/json",
  //           Authorization: `Bearer ${jwt}`,
  //         },
  //         body: JSON.stringify({
  //           group_id: id,
  //         }),
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error("Failed to sync users");
  //     }

  //     const result = await response.json();
  //     const addedUsers = result.data?.added_users || 0;

  //     // Show appropriate toast message
  //     if (addedUsers === 0) {
  //       showSuccess("Group members already up to date");
  //     } else {
  //       showSuccess(`${addedUsers} users added`);
  //     }

  //     // Refresh the users list to show the updated data
  //     fetchGroupUsers(jwt);
  //   } catch (error) {
  //     showError("Failed to sync users. Please try again.");
  //   } finally {
  //     setSyncLoading(false);
  //   }
  // };

  const canManageGroups =
    user && (user.role === "MANAGER" || user.role === "COMPANY_ADMIN");

  if (loading) {
    return (
      <PageContent>
        <Group justify="center" py="xl">
          <Loader size="lg" />
          <Text>Loading group details...</Text>
        </Group>
      </PageContent>
    );
  }

  if (error || !group) {
    return (
      <PageContent>
        <Alert variant="light" color="red" title="Error loading group">
          {error || "Group not found"}
        </Alert>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Stack gap="xl">
        {/* Header with back button and edit */}
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Group gap="md">
              <ActionIcon
                variant="light"
                onClick={() => navigate("/admin?tab=groups")}
                size="md"
              >
                <IconArrowLeft size={18} />
              </ActionIcon>
              <Title order={2}>{group.name || "Unnamed Group"}</Title>
            </Group>
            {canManageGroups && (
              <Button
                leftSection={<IconEdit size={16} />}
                onClick={handleEditGroup}
                variant="light"
              >
                Edit Group
              </Button>
            )}
          </Group>

          {/* Group Info */}
          <Group gap="md" wrap="wrap">
            {group.enable_chat && (
              <Badge color="green" variant="light" size="md">
                Chat Enabled
              </Badge>
            )}
            {group.auto_add && (
              <Badge color="blue" variant="light" size="md">
                Auto Add
              </Badge>
            )}
            {group.include_downline && (
              <Badge color="grape" variant="light" size="md">
                Include Downline
              </Badge>
            )}
            {group.rep_type && (
              <Group gap="xs">
                <div
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "50%",
                    background: group.rep_type.image
                      ? `url(${group.rep_type.image}) center/cover`
                      : "var(--mantine-color-gray-3)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {!group.rep_type.image && <IconUser size={16} />}
                </div>
                <Text size="sm" fw={500}>
                  {group.rep_type.name}
                </Text>
              </Group>
            )}
          </Group>
        </Paper>

        {/* Content Sections */}
        <Stack gap="lg">
          {/* Courses and Programs in a row */}
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            {/* Courses Section */}
            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconBook size={20} color="var(--mantine-color-blue-6)" />
                  <Title order={4}>Courses</Title>
                  <Badge variant="light" size="sm">
                    {group.courses?.length || 0}
                  </Badge>
                </Group>
                {canManageGroups && (
                  <ActionIcon
                    variant="light"
                    color="blue"
                    onClick={handleEditCourses}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
              </Group>
              <Divider mb="md" />
              <Stack gap="sm" mah="300px" style={{ overflowY: "auto" }}>
                {coursesLoading ? (
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                      Loading courses...
                    </Text>
                  </Group>
                ) : group.courses?.length > 0 ? (
                  group.courses.map((course, index) => (
                    <Group
                      key={course.id || index}
                      gap="sm"
                      p="xs"
                      style={{
                        backgroundColor: "var(--mantine-color-gray-0)",
                        borderRadius: "4px",
                      }}
                    >
                      {course.course_thumbnail && (
                        <img
                          src={course.course_thumbnail}
                          alt={course.course_title}
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "4px",
                            objectFit: "cover",
                          }}
                          onError={e => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <Text size="sm">
                        {course.course_title || "Unknown Course"}
                      </Text>
                    </Group>
                  ))
                ) : (
                  <Text
                    size="sm"
                    c="dimmed"
                    ta="center"
                    style={{ fontStyle: "italic" }}
                  >
                    No courses assigned
                  </Text>
                )}
              </Stack>
            </Paper>

            {/* Programs Section */}
            <Paper shadow="sm" p="lg" radius="md" withBorder>
              <Group justify="space-between" mb="md">
                <Group gap="xs">
                  <IconSchool size={20} color="var(--mantine-color-green-6)" />
                  <Title order={4}>Programs</Title>
                  <Badge variant="light" size="sm">
                    {group.programs?.length || 0}
                  </Badge>
                </Group>
                {canManageGroups && (
                  <ActionIcon
                    variant="light"
                    color="green"
                    onClick={handleEditPrograms}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
              </Group>
              <Divider mb="md" />
              <Stack gap="sm" mah="300px" style={{ overflowY: "auto" }}>
                {programsLoading ? (
                  <Group justify="center" py="md">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                      Loading programs...
                    </Text>
                  </Group>
                ) : group.programs?.length > 0 ? (
                  group.programs.map((program, index) => (
                    <Group
                      key={program.id || index}
                      gap="sm"
                      p="xs"
                      style={{
                        backgroundColor: "var(--mantine-color-gray-0)",
                        borderRadius: "4px",
                      }}
                    >
                      <Text size="sm">
                        {program.program_name || "Unknown Program"}
                      </Text>
                    </Group>
                  ))
                ) : (
                  <Text
                    size="sm"
                    c="dimmed"
                    ta="center"
                    style={{ fontStyle: "italic" }}
                  >
                    No programs assigned
                  </Text>
                )}
              </Stack>
            </Paper>
          </SimpleGrid>

          {/* Members Section - Full width below */}
          <Paper shadow="sm" p="lg" radius="md" withBorder>
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <IconUsers size={20} color="var(--mantine-color-orange-6)" />
                <Title order={4}>Members</Title>
                <Badge variant="light" size="sm">
                  {group.users?.length || 0}
                </Badge>
              </Group>
              {canManageGroups && (
                <Group gap="xs">
                  {/* {group.auto_add && (
                    <Button
                      leftSection={<IconRefresh size={16} />}
                      onClick={handleSyncUsers}
                      variant="light"
                      color="blue"
                      size="sm"
                      loading={syncLoading}
                    >
                      Sync
                    </Button>
                  )} */}
                  <ActionIcon
                    variant="light"
                    color="orange"
                    onClick={handleEditMembers}
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Group>
              )}
            </Group>
            <Divider mb="md" />
            <Stack gap="sm" mah="300px" style={{ overflowY: "auto" }}>
              {usersLoading ? (
                <Group justify="center" py="md">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Loading members...
                  </Text>
                </Group>
              ) : group.users?.length > 0 ? (
                group.users.map((member, index) => (
                  <Group
                    key={member.id || index}
                    gap="sm"
                    p="xs"
                    style={{
                      backgroundColor: "var(--mantine-color-gray-0)",
                      borderRadius: "4px",
                    }}
                  >
                    <IconUser size={14} color="var(--mantine-color-blue-6)" />
                    <Text size="sm">
                      {member.first_name && member.last_name
                        ? `${member.first_name} ${member.last_name}`
                        : member.name || "Unknown Member"}
                    </Text>
                  </Group>
                ))
              ) : (
                <Text
                  size="sm"
                  c="dimmed"
                  ta="center"
                  style={{ fontStyle: "italic" }}
                >
                  No members
                </Text>
              )}
            </Stack>
          </Paper>
        </Stack>
      </Stack>

      {/* Modals */}
      <EditGroupModal
        isOpen={editGroupModalOpen}
        onClose={() => setEditGroupModalOpen(false)}
        onGroupUpdated={handleGroupUpdated}
        onGroupDeleted={handleGroupDeleted}
        group={group}
        repTypes={repTypes}
      />

      <GroupCourseModal
        isOpen={editCoursesModalOpen}
        onClose={() => setEditCoursesModalOpen(false)}
        group={group}
        groupCourses={group.courses || []}
        availableCourses={group.unadded_courses || []}
        onCoursesUpdated={handleCoursesUpdated}
      />

      <GroupProgramModal
        isOpen={editProgramsModalOpen}
        onClose={() => setEditProgramsModalOpen(false)}
        group={group}
        groupPrograms={group.programs || []}
        availablePrograms={group.unadded_programs || []}
        onProgramsUpdated={handleProgramsUpdated}
      />

      <EditMembersModal
        isOpen={editMembersModalOpen}
        onClose={() => setEditMembersModalOpen(false)}
        group={group}
        groupUsers={group.users || []}
        availableUsers={group.unadded_users || []}
        onMembersUpdated={handleMembersUpdated}
      />
    </PageContent>
  );
}

export default GroupDetailsPage;
